import { components } from "@apiUtils/schema-staff";
import {
  Button,
  Dialog,
  Drawer,
  IconButton,
  LoadingSkeleton,
  MenuButton,
  Row,
  Select,
} from "@narmi/design_system";
import InfoSection, { InfoRow } from "cerulean/src/InfoSection";
import React from "react";
import { $staffApi } from "../../api";
import { Table } from "../../components/Table";
import { PromptList } from "./PromptList";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { useQueryClient } from "@tanstack/react-query";
import GuideCompletionBadge from "../../components/GuideCompletionBadge";
import styles from "./PromptIndex.module.css";
import { useSegments } from "@hooks/useSegments";

type PromptGroup = components["schemas"]["PromptGroup"];
type GuideTypeFilter = "all" | components["schemas"]["GuideTypeEnum"];

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  timeStyle: "short",
});

const getGuideTypeText = (guideType: PromptGroup["guide_type"]) => {
  if (guideType === "onboarding") {
    return "New users";
  } else if (guideType === "existing") {
    return "Existing users";
  }
  return "-";
};

const getPlatformText = (platforms: PromptGroup["platforms"]) => {
  if (platforms?.length == 2) {
    return "Web & Mobile";
  }
  if (platforms?.[0] === "web") {
    return "Web";
  }
  if (platforms?.[0] === "app") {
    return "Mobile";
  }
  return "";
};

export function PromptIndex() {
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();

  const [drawerPromptGroup, setDrawerPromptGroup] = React.useState<PromptGroup | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(false);
  const [guideTypeFilter, setGuideTypeFilter] = React.useState<GuideTypeFilter>("all");

  const setGuideFilterHandler = (newFilter: GuideTypeFilter) => {
    setGuideTypeFilter(newFilter);
  };

  const { data, isLoading } = $staffApi.useQuery("get", "/api/prompt_groups/");
  const { mutate: deletePromptGroup } = $staffApi.useMutation(
    "delete",
    "/api/prompt_groups/{uuid}/",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get", "/api/prompt_groups/"] });
        setDrawerPromptGroup(null);
        setIsDrawerOpen(false);
        sendNotification({
          type: "success",
          text: "Onboarding guide deleted.",
        });
      },
    }
  );

  const filteredResults: PromptGroup[] = React.useMemo(
    () =>
      data?.results.filter(({ guide_type }) =>
        guideTypeFilter === "all" ? true : guide_type === guideTypeFilter
      ) || [],
    [data?.results, guideTypeFilter]
  );

  const { getSegmentDisplayName } = useSegments();

  const resolvedResults = React.useMemo(
    () =>
      filteredResults.map((promptGroup) => ({
        ...promptGroup,
        segments: promptGroup.segments.map((segmentKey) => getSegmentDisplayName(segmentKey)),
      })),
    [filteredResults, getSegmentDisplayName]
  );

  return (
    <LoadingSkeleton isLoading={isLoading}>
      <div className={styles.PromptFilterSelect}>
        <Select label="Filter guide type" value={guideTypeFilter} onChange={setGuideFilterHandler}>
          <Select.Item value="all">All</Select.Item>
          <Select.Item value="onboarding">New users</Select.Item>
          <Select.Item value="existing">Existing users</Select.Item>
        </Select>
        <IconButton textSize="l" name="rotate-ccw" onClick={() => setGuideFilterHandler("all")} />
      </div>
      <Table>
        <Table.Header>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Segments</Table.HeaderCell>
          <Table.HeaderCell>Platform</Table.HeaderCell>
          <Table.HeaderCell>Applies to</Table.HeaderCell>
          <Table.HeaderCell>Created</Table.HeaderCell>
          <Table.HeaderCell># of Steps</Table.HeaderCell>
        </Table.Header>
        <Table.Body>
          {resolvedResults.length > 0 ? (
            resolvedResults.map((promptGroup) => (
              <Table.Row
                key={promptGroup.uuid}
                onClick={() => {
                  setDrawerPromptGroup(promptGroup);
                  setIsDrawerOpen(true);
                }}
              >
                <Table.Cell>{promptGroup.internal_title}</Table.Cell>
                <Table.Cell>{promptGroup.segments.join(", ")}</Table.Cell>
                <Table.Cell>{getPlatformText(promptGroup.platforms)}</Table.Cell>
                <Table.Cell>{getGuideTypeText(promptGroup.guide_type)}</Table.Cell>
                <Table.Cell>
                  <div>{dateFormatter.format(Date.parse(promptGroup.created_at))}</div>
                  <div>{timeFormatter.format(Date.parse(promptGroup.created_at))}</div>
                </Table.Cell>
                <Table.Cell>{promptGroup.prompt_configurations.length}</Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell>-</Table.Cell>
              <Table.Cell>-</Table.Cell>
              <Table.Cell>-</Table.Cell>
              <Table.Cell>-</Table.Cell>
              <Table.Cell>-</Table.Cell>
              <Table.Cell>-</Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {drawerPromptGroup && data?.results ? (
        <PromptDrawer
          promptGroup={drawerPromptGroup}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          onPrev={
            resolvedResults.indexOf(drawerPromptGroup) > 0
              ? () =>
                  setDrawerPromptGroup(
                    resolvedResults[resolvedResults.indexOf(drawerPromptGroup) - 1]
                  )
              : undefined
          }
          onNext={
            resolvedResults.indexOf(drawerPromptGroup) < resolvedResults.length - 1
              ? () =>
                  setDrawerPromptGroup(
                    resolvedResults[resolvedResults.indexOf(drawerPromptGroup) + 1]
                  )
              : undefined
          }
          onRequestDelete={() =>
            deletePromptGroup({ params: { path: { uuid: drawerPromptGroup.uuid } } })
          }
        />
      ) : null}
    </LoadingSkeleton>
  );
}

function PromptDrawer({
  promptGroup,
  isDrawerOpen,
  setIsDrawerOpen,
  onPrev,
  onNext,
  onRequestDelete,
}: {
  promptGroup: components["schemas"]["PromptGroup"];
  isDrawerOpen: boolean;
  setIsDrawerOpen: (value: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
  onRequestDelete: () => void;
}) {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);

  return (
    <>
      <Drawer
        isOpen={isDrawerOpen}
        onUserDismiss={() => setIsDrawerOpen(false)}
        onPrev={onPrev}
        onNext={onNext}
      >
        <Row alignItems="center">
          <Row.Item>
            <div className="nds-typography">
              <h2>{promptGroup.segments.join(", ")}</h2>
            </div>
          </Row.Item>
          <Row.Item shrink>
            <MenuButton
              label="Actions"
              trigger={<IconButton kind="plain" label="Edit" name="more-vertical" />}
            >
              <MenuButton.Item label="Delete guide" onSelect={() => setIsDeleteConfirmOpen(true)} />
            </MenuButton>
          </Row.Item>
        </Row>
        <InfoSection.Compositional useWhiteBackgroundCards title="Details">
          <InfoRow name="Internal title" value={promptGroup.internal_title} />
          <InfoRow name="External title" value={promptGroup.external_title} />
          <InfoRow name="Description" value={promptGroup.description} />
        </InfoSection.Compositional>
        <InfoSection.Compositional useWhiteBackgroundCards title="Platforms">
          <InfoRow name="Supported platforms" value={getPlatformText(promptGroup.platforms)} />
        </InfoSection.Compositional>
        <InfoSection.Compositional useWhiteBackgroundCards title="Target">
          <InfoRow name="Segments" value={promptGroup.segments.join(", ")} />
          <InfoRow name="Applies to" value={getGuideTypeText(promptGroup.guide_type)} />
        </InfoSection.Compositional>
        <InfoSection.Compositional useWhiteBackgroundCards title="Steps">
          <PromptList>
            {promptGroup.prompt_configurations.map((config) => (
              <PromptList.Item
                key={config.priority}
                title={config.title}
                description={config.description}
                linkText={config.link_text}
                statusTag={
                  config.statistics ? (
                    <GuideCompletionBadge completionRate={config.statistics.completion_rate} />
                  ) : null
                }
              />
            ))}
          </PromptList>
        </InfoSection.Compositional>
      </Drawer>
      <ConfirmDeleteDialog
        isOpen={isDeleteConfirmOpen}
        onDelete={() => onRequestDelete()}
        onClose={() => setIsDeleteConfirmOpen(false)}
      />
    </>
  );
}

const ConfirmDeleteDialog = ({
  isOpen,
  onDelete,
  onClose,
}: {
  isOpen: boolean;
  onDelete: () => void;
  onClose: () => void;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={() => onClose()}
      title="Delete guide"
      footer={
        <div className="margin--top--l">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button onClick={() => onClose()} kind="negative" label="Cancel" />
            </Row.Item>
            <Row.Item shrink>
              <Button
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                kind="primary"
                label="Delete"
              />
            </Row.Item>
          </Row>
        </div>
      }
    >
      Are you sure you want to delete this guide? This cannot be undone.
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
